import { CartLinesUpdateEvent } from '@shopify/events';

// Existing shipping rate is USD 45; use Shopify's current presentment conversion,
// never a stored/manual FX rate or rounded product-price ratio.
function threshold(currency) {
  if (currency === 'USD') return 4500;
  const presentment = window.Shopify?.currency;
  const rate = Number(presentment?.rate);
  return presentment?.active === currency && Number.isFinite(rate) && rate > 0
    ? Math.ceil(4500 * rate) : null;
}
function money(amount, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency, currencyDisplay: 'symbol'
  }).format(amount / 100);
}

class MbtShippingProgress extends HTMLElement {
  static observedAttributes = ['data-total', 'data-currency'];
  connectedCallback() {
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this, { childList: true, subtree: true });
    this.update();
  }
  disconnectedCallback() { this.observer?.disconnect(); }
  attributeChangedCallback() { if (this.isConnected) this.update(); }
  update() {
    const target = threshold(this.dataset.currency);
    const text = this.querySelector('[data-shipping-message]');
    const bar = this.querySelector('progress');
    if (!target || !text || !bar) { this.hidden = true; return; }
    const total = Number(this.dataset.total);
    const remaining = Math.max(0, target - total);
    const message = remaining > 0
      ? this.dataset.remaining.replace('[amount]', money(remaining, this.dataset.currency))
      : this.dataset.unlocked;
    if (text.textContent !== message) text.textContent = message;
    bar.value = Math.min(100, Math.max(0, total / target * 100));
    this.hidden = false;
  }
}

class MbtAddProducts extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('submit', this.submit, { signal: this.controller.signal });
    this.addEventListener('change', this.updatePair, { signal: this.controller.signal });
    if (this.dataset.mainProduct) {
      this.mainVariants = JSON.parse(this.querySelector('[data-main-variants]').textContent);
      this.mainSection = this.closest('.shopify-section');
      this.mainSection?.addEventListener('change', this.updatePair, { signal: this.controller.signal });
    }
    this.updatePair();
  }
  disconnectedCallback() { this.controller?.abort(); }
  getMainVariant() {
    const form = document.getElementById(this.dataset.mainForm);
    const picker = this.mainSection?.querySelector('variant-picker[data-template-product-match="true"]')
      || this.mainSection?.querySelector('variant-picker');
    const selected = picker ? [...picker.querySelectorAll('fieldset')].map(field => field.querySelector('input:checked')?.value) : [];
    if (!selected.length && picker) selected.push(...[...picker.querySelectorAll('select')].map(select => select.value));
    if (selected.length) return this.mainVariants?.find(variant => variant.options.length === selected.length && variant.options.every((option, index) => option === selected[index]));
    return this.mainVariants?.find(variant => String(variant.id) === form?.querySelector('input[name="id"]')?.value);
  }
  updatePair = () => {
    const price = this.querySelector('[data-selected-price]');
    const selected = this.querySelector('select')?.selectedOptions[0];
    if (price && selected?.dataset.formattedPrice) price.textContent = selected.dataset.formattedPrice;
    if (this.dataset.mainProduct) {
      const main = this.getMainVariant();
      const ready = main?.available && selected?.value;
      const total = Number(main?.price || 0) + Number(selected?.dataset.price || 0);
      const formatted = ready ? money(total, this.dataset.currency) : '—';
      for (const output of this.querySelectorAll('[data-pair-total], [data-pair-button-price]')) {
        if (output.textContent !== formatted) output.textContent = formatted;
      }
      const image = this.querySelector('.mbt-matching-image');
      if (image && selected?.dataset.image && image.getAttribute('src') !== selected.dataset.image) {
        image.removeAttribute('srcset');
        image.src = selected.dataset.image;
        image.alt = selected.dataset.imageAlt;
      }
      const shipping = this.querySelector('[data-pair-shipping]');
      if (shipping) shipping.hidden = !ready;
      this.querySelector('button[type="submit"]').disabled = !ready || !!this.pending;
      return;
    }
    if (!this.hasAttribute('data-pair')) return;
    const choices = [...this.querySelectorAll('select')];
    const ready = choices.length === 2 && choices.every(select => select.value);
    const total = choices.reduce((sum, select) => sum + Number(select.selectedOptions[0]?.dataset.price || 0), 0);
    const output = this.querySelector('[data-pair-total]');
    if (output) output.textContent = ready ? money(total, this.dataset.currency) : '';
    const shipping = this.querySelector('[data-pair-shipping]');
    const target = threshold(this.dataset.currency);
    if (shipping) shipping.hidden = !ready || !target || total < target;
  };
  submit = async (event) => {
    event.preventDefault();
    if (this.pending) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.reportValidity()) return;
    const items = [...form.querySelectorAll('select[name="id"]')]
      .map(select => ({ id: Number(select.value), quantity: 1 }));
    if (this.dataset.mainProduct) {
      const main = this.getMainVariant();
      if (!main?.available) return;
      items.unshift({ id: Number(main.id), quantity: 1 });
    }
    if (!items.length || items.some(item => !item.id)) return;
    const error = form.querySelector('[data-add-error]');
    const button = form.querySelector('button[type="submit"]');
    error.hidden = true;
    button.disabled = true;
    this.pending = true;
    const deferred = CartLinesUpdateEvent.createPromise();
    // Horizon's existing cart drawer, counter and product forms consume this event.
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map(item => ({ merchandiseId: String(item.id), quantity: 1 })),
      promise: deferred.promise
    }));
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
        .map(element => element.dataset.sectionId).filter(Boolean))];
      const root = window.Shopify.routes.root;
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
      const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.error);
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: result.sections, itemCount: items.length,
          source: 'mbt-add-products', didError: false }
      });
    } catch (cause) {
      deferred.reject(cause);
      error.textContent = cause.message || this.dataset.error;
      error.hidden = false;
    } finally {
      button.disabled = false;
      this.pending = false;
    }
  };
}

class MbtCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-products'];
  connectedCallback() {
    this.uiController?.abort();
    this.uiController = new AbortController();
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-rec-direction]');
      const carousel = this.querySelector('[data-mbt-carousel]');
      if (!button || !carousel) return;
      carousel.scrollBy({left: Number(button.dataset.recDirection) * carousel.clientWidth,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }, {signal: this.uiController.signal});
    this.contentObserver?.disconnect();
    this.contentObserver = new MutationObserver(() => {
      const list = this.querySelector('[data-recommendations-list]');
      if (list && !list.childElementCount && !this.loading) this.load();
    });
    this.contentObserver.observe(this, {childList: true, subtree: true});
    this.load();
  }
  disconnectedCallback() { this.controller?.abort(); this.uiController?.abort(); this.contentObserver?.disconnect(); }
  attributeChangedCallback() { if (this.isConnected) this.load(); }
  async load() {
    const productId = this.dataset.products?.split(',')[0];
    if (!productId) return;
    this.controller?.abort();
    this.controller = new AbortController();
    const requestController = this.controller;
    this.loading = true;
    try {
      const url = new URL(window.Shopify.routes.root + 'recommendations/products', location.origin);
      url.searchParams.set('section_id', 'mbt-cart-recommendations');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-mbt-recommendations-content]');
      if (content && this.isConnected) {
        this.querySelector('[data-recommendations-list]').replaceChildren(...content.children);
        this.hidden = !this.querySelector('mbt-add-products');
      }
    } catch (error) { if (error.name !== 'AbortError') this.hidden = true; }
    finally { if (this.controller === requestController) this.loading = false; }
  }
}
for (const [name, component] of [['mbt-shipping-progress', MbtShippingProgress], ['mbt-add-products', MbtAddProducts], ['mbt-cart-recommendations', MbtCartRecommendations]]) {
  if (!customElements.get(name)) customElements.define(name, component);
}


